import { Component } from 'react';
import styled from 'styled-components'

import { getCognitoUsers, getCrmUserAdmin } from 'admin/adminapi'
import LinkUserWrapper from 'admin/linkuser/LinkUserWrapper'
import { QueryResultsStyles } from 'admin/QueryStyles'
import { formatDate, formatDateMonth, formatPhone } from 'admin/adminutil'
import AlertModal from 'common/Alert'
import ConfirmModal from 'common/ConfirmModal'
import Alert from 'common/notifications/Alert'
import IdmReactTable from 'common/IdmReactTable'
import { FilledBlue, FilledYellow } from 'css/Buttons'
import * as colors from 'css/Colors'
import * as forms from 'css/forms'
import { primaryBlue } from 'css/Colors'
import iconInfo from 'images/iconColorinfo.svg'
import store from 'redux/store'
import { showLoading } from 'services/util'
import deleteIcon from '../../images/trash-can-icon.png'

import { addReadOnlyAdmin, getReadOnlyAdmins, removeReadOnlyAdmin } from '../mggadminutil'
import css from '../sass/confirm.modal.module.scss'

const defaultFontSize = 16

const hasUclaEmailDomain = email => {
  const domain = email.trim().toLowerCase().split('@').pop()
  return domain === 'ucla.edu' || domain.endsWith('.ucla.edu')
}

const AddUserSearch = styled.div`
  form {
    padding: ${20 / defaultFontSize}rem;
    border-radius: ${5 / defaultFontSize}rem;
    background-color: ${primaryBlue};
  }

  legend {
    display: block;
    padding-left: 0;
    color: #FFFFFF;
  }

  fieldset {
    display: block;
    margin: 0;
    padding: 0;
    border: none;
  }

  .search-fields {
    display: flex;
    align-items: center;
  }

  label {
    flex: 1;
    margin-right: ${10 / defaultFontSize}rem;
  }

  input {
    height: ${50 / defaultFontSize}rem;
    border-radius: 3px;
  }

  .lookup-error {
    margin-top: ${20 / defaultFontSize}rem;
  }
`

const AddButtonWrap = styled.div`
  margin: ${30 / defaultFontSize}rem;
  text-align: center;
`

const AddReadOnlyAdminButton = styled(FilledBlue)`
  text-transform: none;
`

const CognitoResult = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${20 / defaultFontSize}rem;
  padding: ${20 / defaultFontSize}rem;
  border: 1px solid ${colors.lightGray};
  background-color: #FFFFC3;

  & > div {
    display: flex;
    flex: 0 25%;
    flex-direction: column;
    margin-bottom: ${15 / defaultFontSize}rem;
  }

  span:first-child {
    color: ${colors.darkGray};
    font-size: ${12 / defaultFontSize}rem;
  }

  span:last-child {
    font-size: ${14 / defaultFontSize}rem;
  }
`

class ReadOnlyAdmins extends Component {
  state = {
    admins: null,
    adminToRemove: null,
    errorMsg: '',
    isRemoving: false,
    emailLookup: '',
    cognitoUser: null,
    crmUser: null,
    addDialogError: '',
    duplicateAdminEmail: '',
    isSearching: false,
    isAdding: false,
    searchInputKey: 0,
  };

  async componentDidMount() {
    await this.loadAdmins()
  }

  loadAdmins = async () => {
    showLoading(true)

    const response = await getReadOnlyAdmins()

    if (!response || !Array.isArray(response.success)) {
      this.setState({
        admins: null,
        errorMsg: response?.error?.msg || response?.error || 'An Error Occurred',
      })
    }
    else {
      this.setState({admins: response.success, errorMsg: ''})
    }

    showLoading(false)
  }

  handleEmailChange = event => {
    this.setState({
      emailLookup: event.target.value,
      cognitoUser: null,
      crmUser: null,
      addDialogError: '',
    })
  }

  lookupCognitoUser = async event => {
    event.preventDefault()

    const email = this.state.emailLookup.trim()
    if (!this.state.admins || !email || this.state.isSearching) return

    if (!hasUclaEmailDomain(email)) {
      this.setState({
        cognitoUser: null,
        crmUser: null,
        addDialogError: 'Read-Only Admins must have a ucla.edu email address.',
      })
      return
    }

    const emailAlreadyExists = (this.state.admins || []).some(admin =>
      admin.Attributes?.email?.trim().toLowerCase() === email.toLowerCase()
    )

    if (emailAlreadyExists) {
      this.setState({duplicateAdminEmail: email, cognitoUser: null, crmUser: null, addDialogError: ''})
      return
    }

    this.setState({isSearching: true, cognitoUser: null, crmUser: null, addDialogError: ''})

    try {
      const response = await getCognitoUsers(encodeURIComponent(email))
      const cognitoUser = Array.isArray(response)
        ? response.find(user => user.Provider === 'Cognito')
        : null

      if (!cognitoUser) {
        this.setState({
          addDialogError: 'User not found. Read-Only Admins must first create a CIDM account before they can be assigned in the system.',
        })
      }
      else if (!hasUclaEmailDomain(cognitoUser.Attributes?.email || '')) {
        this.setState({addDialogError: 'Read-Only Admins must have a ucla.edu email address.'})
      }
      else {
        const crmId = cognitoUser.Attributes?.primaryCrmId
        const crmResponse = crmId
          ? await getCrmUserAdmin(crmId, store.getState().theme.name)
          : null

        if (!crmResponse?.crmUser) {
          this.setState({
            addDialogError: 'No CRM user found. Donor Portal Read-Only Admins must have their email linked to their CRM account.',
          })
        }
        else if (!crmResponse.crmUser.affiliations?.find(affiliation => affiliation.value === 'Staff')) {
          this.setState({addDialogError: 'User must have affiliation "Staff" in CRM.'})
        }
        else {
          this.setState({cognitoUser, crmUser: crmResponse.crmUser})
        }
      }
    }
    catch (error) {
      this.setState({addDialogError: error.message || 'An Error Occurred'})
    }
    finally {
      this.setState({isSearching: false})
    }
  }

  addAdmin = async () => {
    const {cognitoUser, crmUser} = this.state
    if (!cognitoUser || !crmUser || this.state.isAdding) return

    const primaryName = crmUser.names?.find(name => name.nameType === 'Primary')
    const crmId = crmUser.constituentLookupId || cognitoUser.Attributes?.primaryCrmId
    if (!primaryName?.firstName || !primaryName?.keyName || !crmId) return

    this.setState({isAdding: true, addDialogError: ''})

    try {
      const response = await addReadOnlyAdmin(
        cognitoUser.Username,
        primaryName.firstName,
        primaryName.keyName,
        crmId
      )

      if (!response?.success) {
        const addDialogError = typeof response?.error === 'string'
          ? response.error
          : response?.error?.msg || response?.error?.message || 'An Error Occurred'
        this.setState({addDialogError})
        return
      }

      this.setState(state => ({
        emailLookup: '',
        cognitoUser: null,
        crmUser: null,
        searchInputKey: state.searchInputKey + 1,
      }))
      await this.loadAdmins()
    }
    catch (error) {
      this.setState({addDialogError: error.message || 'An Error Occurred'})
    }
    finally {
      this.setState({isAdding: false})
    }
  }

  confirmRemoveAdmin = admin => {
    this.setState({adminToRemove: admin, errorMsg: ''})
  }

  removeAdmin = async () => {
    const {adminToRemove} = this.state
    if (!adminToRemove || this.state.isRemoving) return

    this.setState({isRemoving: true, errorMsg: ''})

    try {
      const response = await removeReadOnlyAdmin(adminToRemove.Username)

      if (!response?.success) {
        const errorMsg = typeof response?.error === 'string'
          ? response.error
          : response?.error?.msg || response?.error?.message || 'An Error Occurred'
        this.setState({errorMsg})
      }
      else {
        this.setState(state => ({
          admins: state.admins.filter(admin => admin.Username !== adminToRemove.Username),
        }))
      }
    }
    catch (error) {
      this.setState({errorMsg: error.message || 'An Error Occurred'})
    }
    finally {
      this.setState({adminToRemove: null, isRemoving: false})
    }
  }

  render() {
    const columns = [
      {
        Header: 'Name',
        id: 'Name',
        accessor: row => `${row.Attributes?.given_name || ''} ${row.Attributes?.family_name || ''}`.trim()
          || row.Attributes?.name
          || '',
      },
      {
        Header: 'Email / Phone',
        id: 'EmailOrPhone',
        accessor: row => row.Attributes?.email || formatPhone(row.Attributes?.phone_number) || '',
      },
      {Header: 'CRM ID', accessor: row => row.Attributes?.nickname || ''},
      {Header: 'Status', accessor: 'UserStatus'},
      {Header: 'Created', accessor: 'UserCreateDate', Cell: cell => formatDate(cell.value)},
      {Header: 'Last Modified', accessor: 'UserLastModifiedDate', Cell: cell => formatDate(cell.value)},
      {
        Header: ' ',
        id: 'Remove',
        className: 'readonly-admin-remove',
        Cell: ({row}) => (
          <center>
            <img
              width="25"
              height="25"
              title="REMOVE"
              alt="Remove read-only admin"
              src={deleteIcon}
              onClick={() => this.confirmRemoveAdmin(row.original)}
            />
          </center>
        ),
      },
    ]

    const adminEmail = this.state.adminToRemove?.Attributes?.email
      || this.state.adminToRemove?.Username
    const adminName = this.state.adminToRemove
      ? `${this.state.adminToRemove.Attributes?.given_name || ''} ${this.state.adminToRemove.Attributes?.family_name || ''}`.trim()
        || adminEmail
      : ''
    const cognitoUser = this.state.cognitoUser
    const crmUser = this.state.crmUser
    const cognitoEmail = cognitoUser?.Attributes?.email || this.state.emailLookup.trim()
    const primaryName = crmUser?.names?.find(name => name.nameType === 'Primary')
    const crmName = primaryName
      ? `${primaryName.firstName || ''} ${primaryName.keyName || ''}`.trim()
      : ''

    return (
      <LinkUserWrapper>
        <h2>Add Read-Only Admin</h2>
        <p>
          Read-Only Admins must have a <strong>ucla.edu email</strong> that exists on a CRM record with a <strong>Staff</strong> affiliation.
        </p>

        <AddUserSearch>
          <form onSubmit={this.lookupCognitoUser}>
            <fieldset>
              <legend>Enter Email</legend>
              <div className="search-fields">
                <label>
                  <forms.ClearableInput
                    key={this.state.searchInputKey}
                    type="email"
                    name="username"
                    value={this.state.emailLookup}
                    onChange={this.handleEmailChange}
                    placeholder="user@example.com"
                    disabled={!this.state.admins}
                  />
                </label>
                <FilledYellow type="submit" disabled={!this.state.admins || this.state.isSearching}>
                  {this.state.isSearching ? 'SEARCHING...' : 'SEARCH'}
                </FilledYellow>
              </div>
            </fieldset>
          </form>

          {this.state.addDialogError &&
            <Alert
              className="lookup-error"
              nonPromoPage
              padded
              icon={iconInfo}
              color={colors.messagingRed}
              width="25"
              height="25"
              top="35%"
            >
              <div>{this.state.addDialogError}</div>
            </Alert>
          }

          {cognitoUser && crmUser &&
            <>
              <CognitoResult>
                <div>
                  <span>Name</span>
                  <span>{crmName || 'None'}</span>
                </div>
                <div>
                  <span>Email</span>
                  <span className="bold">{cognitoEmail}</span>
                </div>
                <div>
                  <span>CRM ID</span>
                  <span>{crmUser.constituentLookupId || cognitoUser.Attributes?.primaryCrmId}</span>
                </div>
                <div>
                  <span>Date Account Created</span>
                  <span>{formatDateMonth(cognitoUser.UserCreateDate)}</span>
                </div>
              </CognitoResult>

              <AddButtonWrap>
                <AddReadOnlyAdminButton
                  type="button"
                  onClick={this.addAdmin}
                  disabled={this.state.isAdding}
                >
                  {this.state.isAdding
                    ? 'Adding...'
                    : `Add ${crmName.toUpperCase()} To Read-Only Admins`}
                </AddReadOnlyAdminButton>
              </AddButtonWrap>
            </>
          }
        </AddUserSearch>

        <h2>Read-Only Admins</h2>

        {this.state.errorMsg &&
          <Alert icon={iconInfo}>
            <div>{this.state.errorMsg}</div>
          </Alert>
        }

        {this.state.admins &&
          <QueryResultsStyles searchType='readonlyadmins'>
            {this.state.admins.length > 0 &&
              <IdmReactTable
                data={this.state.admins}
                columns={columns}
                initialState={{sortBy: [{id: 'Name', desc: false}]}}
              />
            }

            <section>
              <div><span>Total Results: </span>{this.state.admins.length}</div>
            </section>
          </QueryResultsStyles>
        }

        {this.state.adminToRemove &&
          <ConfirmModal
            onConfirm={this.removeAdmin}
            onCancel={() => this.setState({adminToRemove: null})}
            cancelText="CANCEL"
            confirmText={this.state.isRemoving ? 'REMOVING...' : 'REMOVE'}
            confirmDisabled={this.state.isRemoving}
          >
            <div className={css.modalInner}>
              <div className={css.modalText}>
                Are you sure you want to remove user <strong>{adminName}</strong> from the read only admins group?
              </div>
            </div>
          </ConfirmModal>
        }

        {this.state.duplicateAdminEmail &&
          <AlertModal
            options={
              <FilledBlue
                isSmall={true}
                onClick={() => this.setState({duplicateAdminEmail: ''})}
              >
                OK
              </FilledBlue>
            }
          >
            {this.state.duplicateAdminEmail} is already a Read-Only Admin
          </AlertModal>
        }

      </LinkUserWrapper>
    )
  }
}

export default ReadOnlyAdmins;
